import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Check, CircleDashed, Clock, Download, FileCode, FileText, MessageSquareText, NotebookText,
  Cloud, LoaderCircle, NotebookPen, Pencil, Plus, RotateCcw, Save, Sparkles, Upload, Wand2,
} from 'lucide-react';
import { lessonService, topicService, pdfService, progressService } from '../services/api';
import { useCourse } from '../contexts/CourseContext';
import { useAuth } from '../contexts/AuthContext';
import { AITutor } from '../components/ai/AITutor';
import { SelectionActions } from '../components/ai/SelectionActions';
import { LessonDraft } from '../components/ai/LessonDraft';
import { useTutorOpen, goToSource } from '../components/ai/tutorPage';
import { ListenButton } from '../components/ai/ListenButton';
import {
  AppShell, Badge, Breadcrumbs, Button, Card, EmptyState, FileButton, LinkButton, Loader, Markdown,
  PageHeader, PagerNav, Tabs,
} from '../components/ui';
import './LessonView.css';

// Alvos do tour de edição em cada botão "Editar"
const EDIT_TOUR = { luriSummary: 'add-resumo', transcription: 'add-transcricao', contentMarkdown: 'add-conteudo' };

function LessonView() {
  const { topicId, lessonId, courseSlug } = useParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse } = useCourse();

  // Build navigation path with course slug
  const buildPath = (path) => courseSlug ? `/curso/${courseSlug}${path}` : path;
  const [lesson, setLesson] = useState(null);
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  // Tutor na lateral: lembra se estava aberto; pedidos vêm da seleção de texto e dos botões
  const [tutorOpen, setTutorOpen] = useTutorOpen();
  const [tutorRequest, setTutorRequest] = useState(null);
  const bodyRef = useRef(null);
  const [contentView, setContentView] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const { isAdmin } = useAuth();
  // Anotações pessoais da aula, salvas automaticamente
  const [notes, setNotes] = useState('');
  const [notesState, setNotesState] = useState('idle'); // idle | saving | saved | error
  const notesLoaded = useRef(false);
  const savedNotes = useRef('');

  useEffect(() => {
    if (courseSlug) {
      selectCourse(courseSlug);
    }
    loadData();
  }, [topicId, lessonId, courseSlug]);

  const loadData = async () => {
    try {
      const [lessonData, topicData] = await Promise.all([
        lessonService.getLessonById(lessonId),
        topicService.getTopicById(topicId, true),
      ]);
      setLesson(lessonData);
      setTopic(topicData);
      if (!contentView) {
        setContentView(lessonData.contentMarkdown ? 'markdown' : lessonData.pdfPath ? 'pdf' : null);
      }
      if (courseSlug) {
        try {
          const progress = await progressService.getProgress(lessonId, courseSlug);
          setIsCompleted(progress?.completed || false);
          setNotes(progress?.notes || '');
          savedNotes.current = progress?.notes || '';
        } catch {
          setIsCompleted(false);
        }
        notesLoaded.current = true;
        // Registra a visita: base do "continuar de onde parei"
        progressService.recordView(lessonId, courseSlug).catch(() => {});
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      await lessonService.updateContent(lessonId, { [editing]: editValue });
      setLesson({ ...lesson, [editing]: editValue });
      setEditing(null);
    } catch (error) {
      console.error('Error saving content:', error);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await pdfService.uploadLessonPdf(file, lessonId, topicId);
      await lessonService.updateLesson(lessonId, { pdfPath: result.filename });
      loadData();
    } catch (error) {
      console.error('Error uploading PDF:', error);
    }
  };

  const askTutor = (req) => setTutorRequest({ ...req, at: Date.now() });

  const onSelection = (kind, text) => {
    if (kind === 'explain') askTutor({ message: 'Explique este trecho com outras palavras e um exemplo.', selection: text });
    else if (kind === 'quiz') askTutor({ message: 'Me faça uma pergunta sobre este trecho para eu testar se entendi.', selection: text });
    else askTutor({ mode: 'flashcards', selection: text });
  };

  // "Resumo · §2" → aba Resumo, na 2ª seção; "Transcrição · 04:12" → aba Transcrição, no tempo
  const onSource = (label) => {
    const tab = /^Transcri/i.test(label) ? 'transcription' : /^Conte/i.test(label) ? 'content' : 'summary';
    setActiveTab(tab);
    setEditing(null);
    goToSource(bodyRef, label);
  };

  const handleToggleComplete = async () => {
    if (!courseSlug) return;
    try {
      const result = await progressService.toggleProgress(lessonId, courseSlug);
      setIsCompleted(result.completed);
    } catch (error) {
      console.error('Error toggling progress:', error);
    }
  };

  const findNextLesson = () => {
    if (!topic?.lessons) return null;
    const currentIndex = topic.lessons.findIndex((l) => l.id === parseInt(lessonId));
    if (currentIndex >= 0 && currentIndex < topic.lessons.length - 1) {
      return topic.lessons[currentIndex + 1];
    }
    return null;
  };

  const findPrevLesson = () => {
    if (!topic?.lessons) return null;
    const currentIndex = topic.lessons.findIndex((l) => l.id === parseInt(lessonId));
    if (currentIndex > 0) {
      return topic.lessons[currentIndex - 1];
    }
    return null;
  };

  useEffect(() => {
    if (!notesLoaded.current || !courseSlug || notes === savedNotes.current) return undefined;
    setNotesState('saving');
    const t = setTimeout(async () => {
      try {
        await progressService.saveNotes(lessonId, courseSlug, notes);
        savedNotes.current = notes;
        setNotesState('saved');
      } catch {
        setNotesState('error');
      }
    }, 900);
    return () => clearTimeout(t);
  }, [notes, lessonId, courseSlug]);

  const topicPath = buildPath(`/estudo/${topicId}`);
  const crumbs = [
    { label: 'Modo Estudo', onClick: () => navigate(buildPath('/estudo')) },
    { label: topic?.name || 'Tópico', onClick: () => navigate(topicPath) },
  ];

  if (loading) {
    return (
      <AppShell section="Aula" courseName={currentCourse?.name}>
        <Loader label="Carregando aula..." />
      </AppShell>
    );
  }

  if (!lesson) {
    return (
      <AppShell section="Aula" courseName={currentCourse?.name}>
        <EmptyState
          file="aula.xml"
          title="Aula não encontrada"
          description="Ela pode ter sido removida ou o link está incorreto."
          action={<Button icon={ArrowLeft} onClick={() => navigate(topicPath)}>Voltar para o tópico</Button>}
        />
      </AppShell>
    );
  }

  const nextLesson = findNextLesson();
  const prevLesson = findPrevLesson();
  const lessonIndex = topic?.lessons ? topic.lessons.findIndex((l) => l.id === parseInt(lessonId)) : -1;
  const hasContent = !!(lesson.pdfPath || lesson.contentMarkdown);

  const startEditing = (field, value) => {
    setEditing(field);
    setEditValue(value || '');
  };

  const editActions = (field) => !isAdmin ? null :
    editing === field ? (
      <>
        <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-tour="cancelar">Cancelar</Button>
        <Button size="sm" icon={Save} onClick={handleSave} data-tour="salvar">Salvar</Button>
      </>
    ) : (
      <Button size="sm" variant="outline" icon={Pencil} onClick={() => startEditing(field, lesson[field])} data-tour={EDIT_TOUR[field]}>
        Editar
      </Button>
    );

  const editor = (rows, placeholder) => (
    <textarea
      className="ss-textarea"
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      rows={rows}
      placeholder={placeholder}
    />
  );

  const pdfFrame = (
    <div className="lesson-pdf">
      <iframe src={pdfService.getPdfUrl(lesson.pdfPath)} title="PDF da aula" />
    </div>
  );

  // Qual visualização do conteúdo mostrar, respeitando a escolha do usuário quando há as duas
  const showPdf = lesson.pdfPath && (contentView === 'pdf' || !lesson.contentMarkdown);

  return (
    <AppShell section={topic?.name || 'Aula'} courseName={currentCourse?.name}>
      <div>
        <Breadcrumbs items={[...crumbs, { label: lesson.name }]} />

        <PageHeader
          icon={FileText}
          eyebrow={lessonIndex >= 0 ? `Aula ${lessonIndex + 1} de ${topic.lessons.length}` : 'Aula'}
          title={lesson.name}
          subtitle={lesson.description}
          actions={
            <>
              {lesson.durationMinutes && <Badge tone="neutral" icon={Clock}>{lesson.durationMinutes} min</Badge>}
              <ListenButton kind="lesson" id={lesson.id} available={!!(lesson.contentMarkdown || lesson.luriSummary)} />
              {!tutorOpen && (
                <Button variant="accent" icon={Sparkles} onClick={() => setTutorOpen(true)} data-tour="aula-ia">
                  Tutor
                </Button>
              )}
            </>
          }
        />

        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          tabs={[
            { value: 'summary', label: 'Resumo', icon: NotebookText, tour: 'aula-resumo' },
            { value: 'transcription', label: 'Transcrição', icon: MessageSquareText, tour: 'aula-transcricao' },
            ...(hasContent ? [{ value: 'content', label: 'Conteúdo', icon: FileCode, tour: 'aula-conteudo' }] : []),
            ...(courseSlug ? [{ value: 'notes', label: 'Minhas anotações', icon: NotebookPen }] : []),
            ...(isAdmin ? [{ value: 'generate', label: 'Gerar com IA', icon: Wand2 }] : []),
          ]}
        />

        <Card padding="lg" className="lesson-body" ref={bodyRef}>
          {activeTab === 'summary' && (
            <>
              <div className="lesson-body__head">
                <h2>Resumo da aula</h2>
                <div className="lesson-body__actions">
                  <Button size="sm" variant="accent" icon={Sparkles} onClick={() => askTutor({ mode: 'review' })}>
                    Me pergunte
                  </Button>
                  {editActions('luriSummary')}
                </div>
              </div>
              {editing === 'luriSummary' ? (
                editor(20, 'Cole ou escreva o resumo da aula... Suporta Markdown!')
              ) : lesson.luriSummary ? (
                <Markdown className="lesson-reading">{lesson.luriSummary}</Markdown>
              ) : (
                <EmptyState
                  file="resumo.md"
                  title="Nenhum resumo adicionado ainda"
                  description="Cole o resumo da aula em Markdown para revisar depois ou usar com a IA."
                  action={isAdmin && <Button icon={Plus} onClick={() => startEditing('luriSummary', '')}>Adicionar resumo</Button>}
                />
              )}
            </>
          )}

          {activeTab === 'transcription' && (
            <>
              <div className="lesson-body__head">
                <h2>Transcrição</h2>
                <div className="lesson-body__actions">{editActions('transcription')}</div>
              </div>
              {editing === 'transcription' ? (
                editor(25, 'Cole a transcrição da aula aqui...')
              ) : lesson.transcription ? (
                <div className="lesson-reading lesson-transcription">{lesson.transcription}</div>
              ) : (
                <EmptyState
                  file="transcricao.txt"
                  title="Nenhuma transcrição adicionada ainda"
                  description="Cole a transcrição da aula para pesquisar e revisar o conteúdo falado."
                  action={isAdmin && <Button icon={Plus} onClick={() => startEditing('transcription', '')}>Adicionar transcrição</Button>}
                />
              )}
            </>
          )}

          {activeTab === 'content' && (
            <>
              <div className="lesson-body__head">
                <h2>Conteúdo da aula</h2>
                <div className="lesson-body__actions">
                  {lesson.pdfPath && lesson.contentMarkdown && editing !== 'contentMarkdown' && (
                    <div className="lesson-toggle" role="group" aria-label="Formato do conteúdo">
                      <button
                        type="button"
                        className={contentView === 'markdown' ? 'is-active' : ''}
                        aria-pressed={contentView === 'markdown'}
                        onClick={() => setContentView('markdown')}
                      >
                        <FileCode size={15} aria-hidden="true" /> Texto
                      </button>
                      <button
                        type="button"
                        className={contentView === 'pdf' ? 'is-active' : ''}
                        aria-pressed={contentView === 'pdf'}
                        onClick={() => setContentView('pdf')}
                      >
                        <FileText size={15} aria-hidden="true" /> PDF
                      </button>
                    </div>
                  )}
                  {lesson.pdfPath && (
                    <LinkButton size="sm" variant="ghost" icon={Download} href={pdfService.getPdfUrl(lesson.pdfPath)} target="_blank" rel="noopener noreferrer">
                      Baixar PDF
                    </LinkButton>
                  )}
                  {isAdmin && (
                    <FileButton size="sm" variant="ghost" icon={Upload} accept=".pdf" onChange={handlePdfUpload}>
                      Enviar PDF
                    </FileButton>
                  )}
                  {editActions('contentMarkdown')}
                </div>
              </div>
              {editing === 'contentMarkdown' ? (
                editor(25, 'Cole o conteúdo em Markdown...')
              ) : showPdf ? (
                pdfFrame
              ) : lesson.contentMarkdown ? (
                <Markdown className="lesson-reading">{lesson.contentMarkdown}</Markdown>
              ) : (
                <EmptyState
                  file="conteudo.md"
                  title="Nenhum conteúdo adicionado ainda"
                  description="Adicione o conteúdo em Markdown ou envie um PDF."
                  action={isAdmin && <Button icon={Plus} onClick={() => startEditing('contentMarkdown', '')}>Adicionar conteúdo</Button>}
                />
              )}
            </>
          )}

          {activeTab === 'generate' && isAdmin && (
            <>
              <div className="lesson-body__head">
                <h2>Gerar com IA</h2>
              </div>
              <LessonDraft
                lesson={lesson}
                onPublished={(part, body) => {
                  if (part === 'SUMMARY') setLesson(l => ({ ...l, luriSummary: body.markdown }));
                  if (part === 'CONTENT') setLesson(l => ({ ...l, contentMarkdown: body.markdown }));
                }}
              />
            </>
          )}

          {activeTab === 'notes' && (
            <>
              <div className="lesson-body__head">
                <h2>Minhas anotações</h2>
                <span className={`lesson-notes__state lesson-notes__state--${notesState}`} role="status" aria-live="polite">
                  {notesState === 'saving' && <><LoaderCircle size={14} className="lesson-notes__spin" aria-hidden="true" /> Salvando</>}
                  {notesState === 'saved' && <><Cloud size={14} aria-hidden="true" /> Salvo</>}
                  {notesState === 'error' && 'Não foi possível salvar'}
                </span>
              </div>
              <p className="lesson-notes__hint">Só você vê estas anotações. Elas são salvas automaticamente enquanto você digita.</p>
              <textarea
                className="ss-textarea lesson-notes__text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex.: dúvidas para revisar, exemplos que ajudaram, ligações com outros tópicos..."
                rows={14}
                aria-label="Minhas anotações"
              />
            </>
          )}
        </Card>

        {courseSlug && (
          <div className={`lesson-done ${isCompleted ? 'lesson-done--on' : ''}`} data-tour="aula-concluir">
            <span className="lesson-done__icon" aria-hidden="true">
              {isCompleted ? <Check size={22} strokeWidth={3} /> : <CircleDashed size={22} />}
            </span>
            <div className="lesson-done__text">
              <strong>{isCompleted ? 'Aula concluída' : 'Terminou esta aula?'}</strong>
              <span>{isCompleted ? 'Ela conta no seu progresso do curso.' : 'Marque como concluída para acompanhar seu progresso.'}</span>
            </div>
            <Button variant={isCompleted ? 'outline' : 'accent'} icon={isCompleted ? RotateCcw : Check} onClick={handleToggleComplete}>
              {isCompleted ? 'Desmarcar' : 'Marcar como concluída'}
            </Button>
          </div>
        )}

        <PagerNav
          prev={prevLesson && {
            hint: 'Aula anterior',
            label: prevLesson.name,
            onClick: () => navigate(buildPath(`/estudo/${topicId}/aula/${prevLesson.id}`)),
          }}
          next={nextLesson ? {
            hint: 'Próxima aula',
            label: nextLesson.name,
            onClick: () => navigate(buildPath(`/estudo/${topicId}/aula/${nextLesson.id}`)),
          } : {
            hint: 'Última aula',
            label: 'Concluir tópico',
            done: true,
            onClick: () => navigate(topicPath),
          }}
        />
      </div>

      <SelectionActions containerRef={bodyRef} onAction={onSelection} />
      <AITutor
        ctx={{ lessonId: lesson.id }}
        title={lesson.name}
        reading={`esta aula · ${[lesson.luriSummary && 'resumo', lesson.contentMarkdown && 'conteúdo', lesson.transcription && 'transcrição'].filter(Boolean).join(', ') || 'sem material ainda'}`}
        open={tutorOpen}
        onOpenChange={setTutorOpen}
        request={tutorRequest}
        onSource={onSource}
        hasCode={/```/.test(lesson.contentMarkdown || '') || /```/.test(lesson.luriSummary || '')}
      />
    </AppShell>
  );
}

export default LessonView;
